import { Link } from "@/lib/next-mui/Link";
import { NextLinkButton } from "@/lib/next-mui/NextLinkButton";
import { SignInButton, useUser } from "@/modules/authentication";
import { pricing } from "@/framework/billing/resourcePricing";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { css } from "@emotion/css";
import { HeroSideBySide } from "./HeroSideBySide";
import { AudienceTabs } from "./AudienceTabs";
import { Section } from "./Section";
import Stack from "@mui/material/Stack";
import { PricingContent } from "@/modules/billing/pricing/PricingPageContent";

const headline = `Learn languages with podcasts`;
const subheadlines = [
  `Immerse yourself in the language by listening to what interests you.`,
  `Take advantage of the latest AI technology to generate a transcript and translations.`,
];

const features = [
  {
    title: "Auto-generated transcripts",
    detail:
      "Get a high-quality transcript of any podcast that has an RSS feed.",
  },
  {
    title: "Highlighted words during playback",
    detail:
      "As you listen, the transcript is highlighted with the current word, so you never lose your place.",
  },
  {
    title: "Auto-generated translations",
    detail:
      "If you have trouble understanding the transcript, you can request a machine-generated translation for the paragraph.",
  },
  {
    title: "Look up individual words",
    detail:
      "Look up the meaning of any word in the built-in dictionary (currently only available for German).",
  },
  {
    title: "Content creator features",
    detail: (
      <>
        Create an interactive transcript for your podcast or any other audio
        content. <Link href="/for-creators">Learn more</Link>.
      </>
    ),
  },
];

// Answers may contain links, so they are rendered as nodes, not plain text.
const faq: { question: string; answer: React.ReactNode }[] = [
  {
    question: "What is ScripTea?",
    answer:
      "ScripTea is a web app where you can listen to podcasts, read along in the automatically generated transcript, get machine translations for sentences, and look up individual words in the built-in dictionary.",
  },
  {
    question: "Who is it for?",
    answer:
      "ScripTea is primarily for language learners at the intermediate level or above. It has also features for content creators who want their audio content to be helpful for language learners.",
  },
  {
    question: "What languages are supported?",
    answer:
      "ScripTea supports a multitude of languages. German has the best support, since there is a built-in German-English dictionary. Transcription is available for English, French, German, Hindi, Portuguese, Spanish, Danish, Dutch, Italian, Japanese, Korean, Norwegian, Polish, Swedish, Tamil, Chinese, Indonesian, Russian, Turkish, Ukrainian, Catalan, Arabic, Finnish, Vietnamese, Hebrew, Greek, Malay, Czech, Romanian, Hungarian, Thai, Urdu, Croatian, Bulgarian, Lithuanian, Maori, Welsh, Slovak, Persian, Latvian, Serbian, Azerbaijani, Slovenian, Kannada, Estonian, Macedonian, Icelandic, Armenian, Nepali, Bosnian, Kazakh, Swahili, Galician, Marathi, Afrikaans, Belarusian and Tagalog.",
  },
  {
    question: "What can I listen to?",
    answer:
      "You can use ScripTea with any podcast that has an RSS feed. If a podcast is published to work in all podcatchers (podcast player apps), i.e. it has a public RSS feed, then it works in ScripTea as well. You can browse the trending podcasts, search in a directory, or add any RSS feed URL manually.",
  },
  {
    question: "How much does it cost?",
    answer: (
      <>
        <Link href="/pricing">See the pricing page</Link>.
      </>
    ),
  },
  {
    question: "How is this different from other AI transcription services?",
    answer:
      "ScripTea is built on top of transcription services. In addition, it provides a convenient user interface aimed specifically for language learners. It can transcribe audio as you listen to it, translate any paragraph, and look up the meaning of any word in a dictionary.",
  },
  {
    question: "Is there an iOS or Android app?",
    answer:
      'There is no native iOS or Android app, but you can use ScripTea on your phone in your web browser or install it as a Progressive Web App. To do this on Android, open Chrome, visit https://scriptea.app/app, open the menu and select "Install app". On iOS, open Safari, visit https://scriptea.app/app, tap the share icon in the middle, and pick "Add to Home Screen".',
  },
  {
    question: "How can I contact you?",
    answer:
      'You can email levente@scriptea.app or use the "Contact" functionality in the app. If you have any issues with the app, have an idea on how to improve it, or have any other feedback or question, please don\'t hesitate to contact me.',
  },
];

// const EarlyAccessSignupForm = dynamic(() => import("./EarlyAccessSignupForm"));

export const LandingPageContent = () => {
  return (
    <div
      className={css`
        box-sizing: border-box;
        padding-top: 1rem;
      `}
    >
      <HeroSection />
      <FeaturesSection />
      <PricingSection />
      <FaqSection />

      {/* <GoToAppButton /> */}
    </div>
  );
};

const GoToAppButton = () => {
  return (
    <NextLinkButton href="/app" variant="contained" size="large">
      Open Podcast Player
    </NextLinkButton>
  );
};

const AccountButton = () => {
  const { isLoaded, isSignedIn } = useUser();

  // Rendered only once the session is known, so the label never flips.
  if (!isLoaded) return null;

  return isSignedIn ? (
    <NextLinkButton href="/account" variant="text" size="large">
      My Account
    </NextLinkButton>
  ) : (
    <SignInButton
      title="Sign In"
      variant="outlined"
      size="large"
      color="primary"
      options={{ callbackUrl: "/account" }}
    />
  );
};

const HeroSection = () => {
  return (
    <HeroSideBySide
      headline={headline}
      subheadlines={subheadlines}
      afterLogo={<AudienceTabs activeIndex={0} />}
      callToActionContent={
        <Stack direction="column" spacing={2}>
          <GoToAppButton />
          <AccountButton />
        </Stack>
      }
    />
  );
};

const FeaturesSection = () => {
  return (
    <Section>
      <Typography variant="h4" sx={{ mb: 1 }} component="h4">
        Features
      </Typography>
      {features.map(({ title, detail }, index) => (
        <Box
          key={index}
          sx={{ my: 4 }}
          component={"div"}
          className={"main-point"}
        >
          <Typography variant="h5" sx={{ mb: 1 }} component="h5">
            {title}
          </Typography>
          <Typography component="p">{detail}</Typography>
        </Box>
      ))}
    </Section>
  );
};

const PricingSection = () => {
  return (
    <Section>
      <Typography variant="h4" sx={{ mb: 1 }} component="h4">
        Pricing
      </Typography>

      <PricingContent />
    </Section>
  );
};

const FaqSection = () => {
  return (
    <Section>
      <Typography
        variant="h4"
        sx={{
          mb: 1,
        }}
        component="h4"
      >
        Frequently Asked Questions
      </Typography>
      {faq.map(({ question, answer }, index) => (
        <Box key={index} sx={{ my: 4 }} component="div">
          <Typography variant="h5" sx={{ mb: 1 }} component="h5">
            {question}
          </Typography>
          <Typography component="p">{answer}</Typography>
        </Box>
      ))}
    </Section>
  );
};
